import React from "react";
import { API_URL } from "../config";
import { Link } from "react-router";
import S3Link from "../components/common/links/s3-link";
import ResourceSearchLink from "../components/common/links/resource-search-link";
import GooglePatentLink from "../components/common/links/google-patent-link";
import UsptoPatentPdfLink from "../components/common/links/uspto-patent-pdf-link";
import PriorArtLink from "../components/common/links/prior-art-link";
import { toString, isArray } from "lodash";
import * as Colors from "@unifiedpatents/euclid-react/lib/helpers/colors";
import { Tag } from "antd";

// signature of converter functions should be consistent with antd Table's `render`

export const toTitleCase = val => {
  const symbols = [
    "pllc",
    "p.l.l.c.",
    "plc",
    "p.l.c.",
    "llp",
    "l.l.p.",
    "pc",
    "p.c.",
    "llc",
    "llc.",
    "ip",
    "pa",
    "p.a.",
    "ii",
    "iii",
    "iv",
    "dla",
    "b.v.",
    "b.v",
    "s.a.",
    "s.a",
    "kg",
    "us",
    "usa",
    "u.s.",
    "u.s.a.",
    "us,",
    "usa,",
    "u.s.,",
    "u.s.a.,",
    "ibm",
    "hp",
    "lg",
    "trw",
    "htc",
    "zte",
    "npe"
  ];
  return val
    ? val
      .split(" ")
      .map(w => (symbols.indexOf(w) >= 0 || w.match(/[\d\w-]+\d+/i) ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
      .join(" ")
      .split("-")
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join("-")
    : val;
};

export const toDateString = val => {
  return val ? new Date(val).toISOString().substr(0, 10) : "";
};

export const toClaimNumberString = val => {
  const shrinkClaimNumbers = cns => {
    let start,
      end,
      cnsNew = [];
    cns.forEach((cn, i) => {
      if (i === 0) {
        start = cn;
        end = cn;
      } else if (cn === end + 1) {
        end = cn;
      } else {
        cnsNew.push(start !== end ? `${start}-${end}` : `${end}`);
        start = cn;
        end = cn;
      }
    });
    cnsNew.push(start !== end ? `${start}-${end}` : `${end}`);
    return cnsNew;
  };
  return val && val !== "" ? shrinkClaimNumbers(val).join(", ") : val;
};

export const toSetString = val => {
  return isArray(val) && val.length
    ? val.map((v, i) => (
      <p style={{ margin: 0 }} key={i}>
        {v}
      </p>
    ))
    : val;
};

export const toItemPTABSearchLink = (val, record, index) => {
  return val ? <ResourceSearchLink resource="ptab" value={val} index={index} key={"ips"} /> : val;
};

export const toItemLitigationSearchLink = (val, record, index) => {
  return val ? <ResourceSearchLink resource="litigation" value={val} index={index} key={"ils"} /> : val;
};

export const toCaseDetailLink = val => {
  return val && val !== "" ? (
    <Link to={`/ptab/case/${val}`} key={"cd"}>
      {val}
    </Link>
  ) : (
    val
  );
};

export const toJudgeDetailLink = (val, type) => {
  return val && val !== "" ? (
    <Link
      to={{
        pathname: `/ptab/judge/${val}`,
        query: type ? { type } : undefined
      }}
      key={"jd"}
    >
      {val}
    </Link>
  ) : (
    val
  );
};

export const toPatentDetailLink = val => {
  return val && val !== "" ? (
    <Link to={`/patents/patent/${val}`} key={"pd"}>
      {val}
    </Link>
  ) : (
    val
  );
};

export const toGooglePatentLink = val => {
  return val && val !== "" ? <GooglePatentLink value={val} key={"gp"} /> : val;
};

export const toUsptoPatentPdfLink = val => {
  return val && val !== "" ? <UsptoPatentPdfLink grantNumber={val} key={"upp"} /> : val;
};

export const toPriorArtLink = val => {
  return val && val !== "" ? <PriorArtLink value={val} key={"pa"} /> : val;
};

export const toFilingS3Link = (val, record) => {
  return record && record.pdf_id && record.pdf_id !== "" ? <S3Link fileKey={record.pdf_id} text={val} /> : <span>{val}</span>;
};

export const toReexamDocLink = (val, appNum, docId) => (
  <a target="_blank" rel="noopener noreferrer" href={`${API_URL}/reexam/${appNum}/${docId}`}>
    {val}
  </a>
);

export const toUPCDecisionOrderS3Link = (s3Key) => {
  const s3KeyWithoutExtension = s3Key ? s3Key.split("/")[1].replace(".pdf", "") : "";
  return s3Key && s3Key !== "" ? <S3Link fileKey={`upc-decisions-orders/${s3Key}`} text={s3KeyWithoutExtension} /> : <span>{s3KeyWithoutExtension}</span>;
};

export const toGenericLink = (val, link) => link ? <a target="_blank" rel="noopener noreferrer" href={`${link}`}>{val}</a> : <div>{val}</div>

export const toValueOption = val => {
  let valStr = toString(val);
  return valStr === "0" ? "FALSE" : valStr === "1" ? "TRUE" : valStr.toUpperCase();
};

export const toSplittedNumbers = val => val
  ? val.trim()
    .split(/\r?\n/)
    .map(e => e.trim().replace(/[,.\s]/g, ""))
    .filter(s => s.length > 0)
  : undefined;

const PTAB_STATUS_TEXT_COLOR_MAP = {
  "Not Instituted - Merits": Colors.grey500,
  "Not Instituted - Procedural": Colors.grey500,
  "Procedural Termination": Colors.grey500,
  "Adverse Judgment": Colors.blue200,
  "Final Written Decision": Colors.blue500,
  Settlement: Colors.blue300,
  Pending: Colors.orange400,
  "Pending - Instituted": Colors.orange200,
  Other: Colors.grey200
};

export const colorPTABStatus = val => (
  <Tag key="status" color={PTAB_STATUS_TEXT_COLOR_MAP[val]}>
    {val}
  </Tag>
);

export const toUSDCurrency = val =>
  val.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });

export const toReexamDetailLink = val => {
  return val && val !== "" ? (
    <Link to={`/exparte/${val}`} key={"re"}>
      {val}
    </Link>
  ) : (
    val
  );
};

export const toUPCDetailLink = val => {
  // Convert the forward slash into the URL-encoded representation of it.
  return val && val !== "" ? (
    <Link to={`/upc/case/${val.replace("/", "%2F")}`} key={"upc-detail"}>
      {val}
    </Link>
  ) : (
    val
  );
};

export const toSetUPCDetailLinkString = val => {
  return isArray(val) && val.length
    ? val.map((v, i) => (
      <div key={i}>
        {toUPCDetailLink(v)}
      </div>
    ))
    : val;
};

export const toSetUPCDocumentLinkString = (titles, s3Key) => {
  return isArray(titles) && titles.length ? titles.map((doc, i) => (
    <div key={i}>
      {s3Key ? <S3Link fileKey={`upc-decisions-orders/${s3Key}`} text={doc} /> : <div>{doc}</div>}
    </div>
  )) : "N/A";
};

export const toSetUPCDecisionsOrdersAuthorsString = val => {
  const authors = [...new Set(val.flatMap(item => item.authors))];
  return isArray(val) && val.length
    ? <div key={val.decision_order_reference_number}>
      {toSetString([...new Set(authors)])}
    </div>
    : "N/A";
};

export const toUPCStageWithReferenceString = (stage, reference, link) => {
  return reference && link ? <><div>{stage}</div>{toGenericLink(`Ref: ${reference}`, link)}</> : <><div>{stage}</div>{reference ? <div>Ref: {reference}</div> : null}</>;
}

export const generateRandomString = (length = 8) => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return result;
};

export const flattenClaims = (claims, parentIndex = '') => {
  return claims.reduce((acc, claim, index) => {
    const key = parentIndex ? `${parentIndex}-${index}` : `${index}`;
    acc.push({ key, text: claim.text });
    if (claim.children && claim.children.length) {
      acc = acc.concat(flattenClaims(claim.children, key));
    }
    return acc;
  }, []);
};
